import {
  Avatar,
  Button,
  Divider,
  Dropdown,
  Layout,
  message,
  theme,
  Typography
} from "antd";
import { setCookie } from "cookies-next";
import React, { useContext, useEffect, useState } from "react";
import { FiLogOut } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import logo from "../Logo.svg";
import AWSCognitoUserPool from "../shared/api/AWSCognitoUserPool";
import { FbContext } from "../shared/rbac/context";
import { fetchConfig } from '../shared/store/configSlice'; // adjust path
import { fetchAllKb } from "../shared/store/kbSlice";
import { setStep } from "../shared/store/onboardSlice";

const { Header } = Layout;
const { Paragraph } = Typography;

const getAvatarBackgroundColor = (email) => {
  if (!email || typeof email !== "string" || email.length === 0) {
    return "#808080";
  }
  const firstLetter = email.charAt(0).toUpperCase();
  const charCode = firstLetter.charCodeAt(0);
  const hue = (charCode * 36) % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

// Styled Components
const DropdownContainer = styled.div`
  padding: 16px;
  width: 220px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const ProfileAvatar = styled(Avatar)`
  background-color: ${(props) => getAvatarBackgroundColor(props.email)};
  margin-right: 16px;
  font-size: 20px;
  font-weight: 600;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const UserNameText = styled(Paragraph)`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 0 !important;
`;

const EmailText = styled(Paragraph)`
  margin-bottom: 0;
  font-size: 14px;
  color: #555;
`;

const StyledDivider = styled(Divider)`
  margin: 12px 0;
`;

const LogoutButton = styled(Button)`
  width: 100%;
  border-radius: 6px;
  font-weight: 600;
  background-color: #fafafa;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgb(252, 108, 51) !important;
    color: #ffffff !important;
  }
`;

const HeaderLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const FaceBotCount = useSelector((state) => state.config.facebot_count);
  const allKbData = useSelector((state) => state.allKb.data);
  const authCxt = useContext(FbContext);
  const [userData, setUserData] = useState({});
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  useEffect(() => {
    dispatch(fetchConfig());
    dispatch(fetchAllKb());
   
  }, [dispatch]);

  useEffect(() => {
    const checkFaceBotCount = async() => {
      if (FaceBotCount === 0) {
        message.info("Redirecting to onboarding page...");
        navigate("/onboard");
        if(allKbData.length === 0) {
          setCookie("fb_kb_id", allKbData[0]?.kb_id);
          dispatch(setStep(0));
        } else if(allKbData.length === 1) {
          dispatch(setStep(2));
        }
      }
    };

    checkFaceBotCount();
  }, [FaceBotCount]);

  const cognitoUser = AWSCognitoUserPool.getCurrentUser();

  useEffect(() => {
    if (cognitoUser) {
      cognitoUser.getSession((err, session) => {
        if (err) return;

        const userEmail = session.getIdToken().payload.email;
        const userName = session.getIdToken().payload.given_name;
        const userPhone = session.getIdToken().payload.phone_number;

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            console.error("Error getting user attributes:", err);
            return;
          }

        });

        setUserData({ email: userEmail, name: userName, phone: userPhone });
      });
    }
  }, []);

  const handleLogOut = () => {
    if (cognitoUser) {
      try {
        cognitoUser.signOut();
        authCxt.logout();
        message.success("Successfully logged out!");
        window.location.href = "/";
      } catch (error) {
        message.error(`Logout error: ${error.message}`);
      }
    }
    navigate("/");
  };

  const dropdownItems = [
    {
      key: "profile",
      label: (
        <DropdownContainer>
          <ProfileCard>
            <ProfileAvatar size={48} email={userData.email}>
              {userData.email?.charAt(0).toUpperCase()}
            </ProfileAvatar>
            <div>
              <UserNameText>{userData.name}</UserNameText>
              <EmailText>{userData.email || "N/A"}</EmailText>
            </div>
          </ProfileCard>

          <StyledDivider />

          <LogoutButton
            type="default"
            icon={<FiLogOut />}
            onClick={handleLogOut}
          >
            Log out
          </LogoutButton>
        </DropdownContainer>
      ),
      icon: null,
      disabled: true,
    },
  ];

  const avatarBgColor = getAvatarBackgroundColor(userData.email);

  return (
    <Header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: colorBgContainer,
      }}
    >
      {/* Logo Section */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <img
          src={logo}
          alt="Logo"
          style={{ height: "40px", marginRight: "12px" }}
        />
      </div>

      {/* User Avatar Section */}
      <Dropdown
        menu={{ items: dropdownItems }}
        placement="bottomRight"
        trigger={["click"]}
        overlayStyle={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <Avatar
          size={32}
          shape="circle"
          style={{
            cursor: "pointer",
            backgroundColor: avatarBgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          {userData.email?.charAt(0).toUpperCase()}
        </Avatar>
      </Dropdown>
    </Header>
  );
};

export default HeaderLayout;
